/**
 * Versões da peça e o que mudou entre dois textos.
 *
 * Um motor só para duas perguntas: "o que a pessoa mudou à mão desde que a IA
 * leu o texto" (`alteracoesDesdeIA`) e "o que mudou entre esta versão e o
 * texto atual" (painel Versões). As duas respostas saem por TRECHO — o
 * parágrafo inteiro como estava e como ficou —, que é o que a IA precisa para
 * reescrever e o que um advogado lê para conferir.
 *
 * A diferença de documento usa o `prosemirror-changeset` (vem no @tiptap/pm,
 * peer que o editor já tem); a de palavras, dentro de um parágrafo, é uma
 * subsequência comum simples.
 */
import { ChangeSet, type Change } from '@tiptap/pm/changeset';
import { DOMSerializer, type Node as PMNode, type Schema } from '@tiptap/pm/model';
import { StepMap } from '@tiptap/pm/transform';
import { estiloPersonalizado } from './estilos';

/* ---------- Tipos públicos ---------- */

export type OrigemVersao = 'abertura' | 'salvamento' | 'automatico' | 'nomeada' | 'ia' | 'restauracao';

/** Uma versão guardada da peça. O app guarda a lista; o editor cria e lê. */
export interface VersaoDoTexto {
  id: string;
  /** ISO 8601. */
  criadaEm: string;
  /**
   * HTML de saída (o mesmo do `onChange`). Ausente numa versão que o app lista
   * sem o texto: ele vem por `carregarVersao` quando a pessoa a abre.
   */
  html?: string;
  /** Palavras do texto, quando o app as conta (a lista sem o `html`). */
  palavras?: number;
  origem: OrigemVersao;
  /** Nome dado pela pessoa ("Antes da réplica") ou pelo editor. */
  rotulo?: string;
  /** Quem salvou, quando o app sabe dizer. */
  autor?: string;
}

export interface TrechoAlterado {
  /** O parágrafo (ou parágrafos) como estava. Vazio em `incluido`. */
  antes: string;
  /** Como ficou. Vazio em `removido`. */
  depois: string;
  /** O título de seção mais próximo acima do trecho ("DOS FATOS"). */
  secao?: string;
  tipo: 'alterado' | 'incluido' | 'removido';
}

export interface AlteracoesDoUsuario {
  houve: boolean;
  trechos: TrechoAlterado[];
  /** Uma frase para o chat: "2 trechos alterados em DOS FATOS e 1 incluído." */
  resumo: string;
  /** O texto inteiro de agora, para quem preferir mandar tudo. */
  texto: string;
}

export const ROTULO_DA_ORIGEM: Record<OrigemVersao, string> = {
  abertura: 'Como estava ao abrir',
  salvamento: 'Salva',
  automatico: 'Salvamento automático',
  nomeada: 'Versão com nome',
  ia: 'Antes da IA escrever',
  restauracao: 'Antes de voltar a uma versão',
};

/* ---------- Origem das transações ---------- */

/**
 * Meta das transações que a IA faz no texto (escrever, sugestão aceita ou
 * recusada). Elas não contam como "a pessoa mexeu".
 */
export const ORIGEM_IA = 'cm-ef-origem-ia';

export type Autoria = 'usuario' | 'ia';

export function novaBaseDaIA(doc: PMNode): ChangeSet<Autoria> {
  // Trecho mexido pelos dois conta como da pessoa: ela também mudou ali.
  return ChangeSet.create<Autoria>(doc, (a, b) => (a === 'usuario' || b === 'usuario' ? 'usuario' : 'ia'));
}

const daPessoa = (c: Change<Autoria>) =>
  c.inserted.some((s) => s.data === 'usuario') || c.deleted.some((s) => s.data === 'usuario');

export function houveAlteracaoDoUsuario(cs: ChangeSet<Autoria> | null): boolean {
  return !!cs && cs.changes.some(daPessoa);
}

/* ---------- Trechos ---------- */

/** Índices (inclusive) dos blocos de topo que a faixa toca; `null` se nenhum. */
function blocosTocados(doc: PMNode, from: number, to: number): [number, number] | null {
  const a = doc.resolve(from);
  if (from === to) return a.depth > 0 ? [a.index(0), a.index(0)] : null;
  const b = doc.resolve(to);
  const ini = a.index(0);
  const fim = b.depth > 0 ? b.index(0) : b.index(0) - 1;
  return fim >= ini ? [ini, fim] : null;
}

const textoDoBloco = (n: PMNode) => n.textBetween(0, n.content.size, ' ', '').replace(/\s+/g, ' ').trim();

function ehTitulo(n: PMNode): boolean {
  const e = n.attrs?.estilo as string | undefined;
  if (!e) return false;
  return e === 'titulo' || estiloPersonalizado(e)?.baseadoEm === 'titulo';
}

function secaoAcima(doc: PMNode, indice: number): string | undefined {
  for (let i = Math.min(indice, doc.childCount - 1); i >= 0; i--) {
    const n = doc.child(i);
    if (ehTitulo(n)) return textoDoBloco(n) || undefined;
  }
  return undefined;
}

interface Grupo { a: [number, number] | null; b: [number, number] | null; ancoraB: number }

/** Junta as mudanças por bloco: duas mudanças no mesmo parágrafo viram um trecho. */
function agrupar(docA: PMNode, docB: PMNode, changes: readonly Change[]): Grupo[] {
  const grupos: Grupo[] = [];
  for (const c of changes) {
    const a = blocosTocados(docA, c.fromA, c.toA);
    const b = blocosTocados(docB, c.fromB, c.toB);
    if (!a && !b) continue;
    const ancoraB = b ? b[0] : docB.resolve(c.fromB).index(0);
    const ult = grupos[grupos.length - 1];
    const encosta = (x: [number, number] | null, y: [number, number] | null) => !!x && !!y && y[0] <= x[1];
    if (ult && (encosta(ult.a, a) || encosta(ult.b, b))) {
      ult.a = ult.a && a ? [Math.min(ult.a[0], a[0]), Math.max(ult.a[1], a[1])] : ult.a ?? a;
      ult.b = ult.b && b ? [Math.min(ult.b[0], b[0]), Math.max(ult.b[1], b[1])] : ult.b ?? b;
    } else grupos.push({ a, b, ancoraB });
  }
  return grupos;
}

const juntar = (doc: PMNode, f: [number, number] | null) => {
  if (!f) return '';
  const partes: string[] = [];
  for (let i = f[0]; i <= f[1]; i++) partes.push(textoDoBloco(doc.child(i)));
  return partes.join('\n\n');
};

export function trechosEntre(docA: PMNode, docB: PMNode, changes: readonly Change[]): TrechoAlterado[] {
  const trechos: TrechoAlterado[] = [];
  for (const g of agrupar(docA, docB, changes)) {
    const antes = juntar(docA, g.a);
    const depois = juntar(docB, g.b);
    // Só formatação (negrito, estilo do parágrafo): o texto não mudou.
    if (antes === depois) continue;
    const tipo = !antes ? 'incluido' : !depois ? 'removido' : 'alterado';
    trechos.push({ antes, depois, secao: secaoAcima(docB, g.ancoraB - (g.b ? 0 : 1)), tipo });
  }
  return trechos;
}

/** Diferença entre dois documentos quaisquer (versão × atual). */
export function mudancasEntre(docA: PMNode, docB: PMNode): readonly Change[] {
  // Um passo que troca tudo; o changeset compara o conteúdo e devolve só o que mudou.
  const mapa = new StepMap([0, docA.content.size, docB.content.size]);
  return ChangeSet.create(docA).addSteps(docB, [mapa], null).changes;
}

export function alteracoesDaPessoa(cs: ChangeSet<Autoria> | null, docAtual: PMNode): AlteracoesDoUsuario {
  const texto = docAtual.textBetween(0, docAtual.content.size, '\n\n', '');
  if (!cs) return { houve: false, trechos: [], resumo: 'Nada mudou desde a última leitura da IA.', texto };
  const trechos = trechosEntre(cs.startDoc, docAtual, cs.changes.filter(daPessoa));
  return { houve: trechos.length > 0, trechos, resumo: resumir(trechos), texto };
}

export function resumir(trechos: TrechoAlterado[]): string {
  if (!trechos.length) return 'Nada mudou desde a última leitura da IA.';
  const conta = (t: TrechoAlterado['tipo']) => trechos.filter((x) => x.tipo === t).length;
  const partes: string[] = [];
  const n = { alterado: conta('alterado'), incluido: conta('incluido'), removido: conta('removido') };
  if (n.alterado) partes.push(`${n.alterado} ${n.alterado === 1 ? 'trecho alterado' : 'trechos alterados'}`);
  if (n.incluido) partes.push(`${n.incluido} ${n.incluido === 1 ? 'incluído' : 'incluídos'}`);
  if (n.removido) partes.push(`${n.removido} ${n.removido === 1 ? 'removido' : 'removidos'}`);
  const secoes = Array.from(new Set(trechos.map((t) => t.secao).filter(Boolean))) as string[];
  const onde = secoes.length === 1 ? ` em ${secoes[0]}` : secoes.length > 1 ? ` em ${secoes.length} seções` : '';
  const frase = partes.length > 1 ? `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}` : partes[0];
  return `Você mudou o texto: ${frase}${onde}.`;
}

/* ---------- Comparação para a tela ---------- */

export type ParteDaPalavra = { tipo: 'igual' | 'ins' | 'del'; texto: string };
export type BlocoComparado =
  | { tipo: 'igual'; html: string }
  | { tipo: 'mudou'; html: string; trecho: TrechoAlterado['tipo'] };

const escapar = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** Palavra a palavra, por subsequência comum. Texto grande demais: tudo saiu, tudo entrou. */
export function diferencaDePalavras(antes: string, depois: string): ParteDaPalavra[] {
  const a = antes.split(/(\s+)/).filter(Boolean);
  const b = depois.split(/(\s+)/).filter(Boolean);
  if (a.length * b.length > 4_000_000) {
    return [...(antes ? [{ tipo: 'del' as const, texto: antes }] : []), ...(depois ? [{ tipo: 'ins' as const, texto: depois }] : [])];
  }
  const m = a.length;
  const n = b.length;
  const L = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out: ParteDaPalavra[] = [];
  const por = (tipo: ParteDaPalavra['tipo'], t: string) => {
    const u = out[out.length - 1];
    if (u && u.tipo === tipo) u.texto += t; else out.push({ tipo, texto: t });
  };
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { por('igual', a[i]); i++; j++; } else if (L[i + 1][j] >= L[i][j + 1]) { por('del', a[i]); i++; } else { por('ins', b[j]); j++; }
  }
  while (i < m) por('del', a[i++]);
  while (j < n) por('ins', b[j++]);
  return out;
}

/** O documento B, com o que mudou desde A marcado palavra a palavra. */
export function compararParaTela(docA: PMNode, docB: PMNode, schema: Schema): BlocoComparado[] {
  const ser = DOMSerializer.fromSchema(schema);
  const elemento = (n: PMNode) => ser.serializeNode(n) as HTMLElement;
  const html = (n: PMNode) => elemento(n).outerHTML;
  const grupos = agrupar(docA, docB, mudancasEntre(docA, docB));
  const blocos: BlocoComparado[] = [];
  let k = 0; // próximo bloco de B ainda não desenhado
  const iguaisAte = (fim: number) => { for (; k < fim; k++) blocos.push({ tipo: 'igual', html: html(docB.child(k)) }); };
  for (const g of grupos) {
    const ini = g.b ? g.b[0] : g.ancoraB;
    iguaisAte(ini);
    const antes = juntar(docA, g.a);
    const depois = juntar(docB, g.b);
    if (g.b) {
      if (antes === depois) { iguaisAte(g.b[1] + 1); continue; }
      k = g.b[1] + 1;
    }
    const partes = diferencaDePalavras(antes, depois);
    // Quebra em parágrafos onde o texto tinha "\n\n".
    const paragrafos: ParteDaPalavra[][] = [[]];
    for (const p of partes) {
      const pedacos = p.texto.split('\n\n');
      pedacos.forEach((t, idx) => {
        if (idx > 0) paragrafos.push([]);
        if (t) paragrafos[paragrafos.length - 1].push({ tipo: p.tipo, texto: t });
      });
    }
    // O parágrafo marcado sai com a forma do original (recuo, alinhamento, fonte do estilo).
    const ref = g.b ? docB.child(g.b[0]) : g.a ? docA.child(g.a[0]) : null;
    const el = ref?.type.name === 'paragraph' ? elemento(ref) : null;
    const attrs = el ? Array.from(el.attributes).map((a) => ` ${a.name}="${escapar(a.value)}"`).join('') : '';
    const pintar = (x: ParteDaPalavra) => (x.tipo === 'igual' ? escapar(x.texto) : `<${x.tipo}>${escapar(x.texto)}</${x.tipo}>`);
    blocos.push({
      tipo: 'mudou',
      html: paragrafos.filter((p) => p.length).map((p) => `<p${attrs}>${p.map(pintar).join('')}</p>`).join(''),
      trecho: !antes ? 'incluido' : !depois ? 'removido' : 'alterado',
    });
  }
  iguaisAte(docB.childCount);
  return blocos;
}

/** Contagem que o painel mostra ao lado de cada versão. */
export function palavrasDoHtml(html: string): number {
  return (html.replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/g, ' ').match(/[A-Za-zÀ-ÿ0-9]+/g) ?? []).length;
}
